import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import DetectRTC from 'detectrtc';
import { useSnackbar } from 'notistack';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AIScheduleInvite } from 'src/@types/ai-schedules';
import {
  AICallChannel,
  ChatChannelType,
  IChatConversation,
} from 'src/@types/chat';
import { CustomStatus, UserStatus } from 'src/@types/user-status';
import { IWorkspace } from 'src/@types/workspace';
import { IWorkspaceUser, UserType } from 'src/@types/workspace-info';
import { useAuthContext } from 'src/auth/useAuthContext';
import { LIVEKIT_AI_SERVER_URL } from 'src/config-global';
import { AudioType, useAudio } from 'src/features/audio';
import { useGatheringPermissions } from 'src/hooks/useGatheringPermissions';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { AIScheduleRequests } from 'src/requests/ai-schedules';
import { ChatRequests } from 'src/requests/chat';
import { useSocketEvent } from 'src/socket';
import { onSocketReconnect } from 'src/socket/listeners/common';
import {
  startTakingNotes,
  stopTakingNotes,
} from 'src/socket/listeners/notes-transcription';
import {
  VoiceControl,
  onKickParticipant,
  onMuteParticipant,
} from 'src/socket/listeners/voice-channel';
import { useSocketIO } from 'src/socket/provider';
import useUIState from 'src/state/ui/persistUiState';
import {
  ConnectionStatus,
  VoiceChannel,
  VoiceChannelMember,
} from './voice-channel-context.types';

type VoiceChannelContextType = {
  // All Voice channels
  voiceChannels: VoiceChannel[];
  setVoiceChannels: Dispatch<SetStateAction<VoiceChannel[]>>;

  // Connect and Disconnect
  connectAiCall(
    data: AIScheduleInvite & { aiUser: IWorkspaceUser; isSchedule?: boolean },
  ): void;
  connectVoiceChannel(data: {
    channel: IChatConversation;
    isGuest?: boolean;
    callOtherMembers?: boolean;
  }): void;
  disconnectVoiceChannel(): void;
  // disconnectRtcOnly(): void;

  setMuted(newMutedValue: boolean, sendSocket?: boolean): void;
  setDeafed(newDeafedValue: boolean): void;
  startScreenShare(): void;
  stopScreenShare(): void;
  startCamera(): void;
  stopCamera(): void;
};

const initialState: VoiceChannelContextType = {
  // All Voice channels
  voiceChannels: [],
  setVoiceChannels() {},

  // Connect and Disconnect
  connectAiCall() {},
  connectVoiceChannel() {},
  disconnectVoiceChannel() {},
  // disconnectRtcOnly() {},

  setMuted() {},
  setDeafed() {},
  startScreenShare() {},
  stopScreenShare() {},
  startCamera() {},
  stopCamera() {},
};

const VoiceChannelContext = createContext(initialState);

export function useVoiceChannelContext() {
  const context = useContext(VoiceChannelContext);

  if (!context)
    throw new Error(
      'useVoiceChannelContext must be use inside VoiceChannelContext',
    );

  return context;
}

type Props = {
  children: React.ReactNode;
};

export function VoiceChannelContextProvider({ children }: Props) {
  const { user } = useAuthContext();
  const { io } = useSocketIO();
  const audio = useAudio();
  const { enqueueSnackbar } = useSnackbar();

  const { hasMicrophone, grantPermission } = useGatheringPermissions();

  const {
    connectionObject: guestConnectionObject,
    setConnectionObject,
    myMeetId,
    createConnection,
    activeVoiceChannel,
    setActiveVoiceChannel,
    connectionStatus,
    setConnectionStatus,
    connectionQuality,
    localParticipant,
    updateLocalParticipant,
    disconnect,
    setMute,
    setDeaf,
    switchVideo,
    stopVideo,
    setVideoFilter,
    setFilterParams,
    setPreConnectionOptions,
    defaultDevice,
    changeDevice,
  } = useLivekitRTCState();

  const {
    setNote,
    setActiveTranscriptionChannelId,
    setActiveTranscriptionOwner,
    setActiveTranscriptionNote,
    activeTranscriptionOwner,
    activeTranscriptionNote,
  } = useTranscriptionsState();

  const { videoFilter, filterParams, micMuted, setMicMuted } = useUIState();

  const [voiceChannels, setVoiceChannels] = useState<VoiceChannel[]>([]);
  const [alreadyMutedWhenDeaf, setAlreadyMutedWhenDeaf] = useState(false);

  const localParticipantSocket = useRef({
    deaf: false,
    muted: false,
    sharing: false,
    camera: false,
    raisedHand: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    DetectRTC.load(() => {
      if (!DetectRTC.hasWebcam) return;
      const selectedVideo = DetectRTC.videoInputDevices.find(
        (video) => video.id === defaultDevice.video,
      );
      if (!selectedVideo) {
        changeDevice('videoinput', DetectRTC.videoInputDevices[0].id);
      }
    });
    setVideoFilter(videoFilter);
    setFilterParams(videoFilter, filterParams);
  }, []);

  useEffect(() => {
    if (!activeVoiceChannel?.channel?._id || !localParticipant) return;
    const { deaf, muted, sharing, camera, meetId, identity, raisedHand } =
      localParticipant;
    const member = {
      deaf,
      muted,
      sharing,
      camera,
      meetId: meetId || myMeetId,
      identity,
      raisedHand,
    };
    if (
      JSON.stringify(member) === JSON.stringify(localParticipantSocket.current)
    )
      return;
    localParticipantSocket.current = member;
    io.emit(`/voice-channel/member/update`, {
      channelId: activeVoiceChannel.channel._id,
      member,
    });
    updatedMemberInfo(activeVoiceChannel.channel._id, user?._id || '', member);
  }, [activeVoiceChannel, localParticipant, myMeetId, io.emit, user?._id]);

  const updateActiveVoiceChannel = useCallback(
    (newVoiceChannels: VoiceChannel[]) => {
      if (activeVoiceChannel?.channel?._id) {
        const updatedActiveVoiceChannel = newVoiceChannels.find(
          (vc) => vc.channel._id === activeVoiceChannel.channel._id,
        );
        if (updatedActiveVoiceChannel)
          setActiveVoiceChannel(updatedActiveVoiceChannel);
      }
    },
    [activeVoiceChannel?.channel?._id, setActiveVoiceChannel],
  );

  useEffect(() => {
    updateActiveVoiceChannel(voiceChannels);
  }, [voiceChannels, updateActiveVoiceChannel]);

  function removeMemberFromVoiceChannel(channelId: string, memberId: string) {
    setVoiceChannels((prevVoiceChannel) =>
      prevVoiceChannel.map((vc) =>
        vc.channel._id === channelId
          ? {
              ...vc,
              members: vc.members.filter((m) => m.user._id !== memberId),
            }
          : vc,
      ),
    );
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const updatedMemberInfo = useCallback(
    (
      channelId: string,
      memberId: string,
      info: Partial<VoiceChannelMember>,
    ) => {
      setVoiceChannels((prevVoiceChannel) =>
        prevVoiceChannel.map((vc) =>
          vc.channel._id === channelId
            ? {
                ...vc,
                ...activeVoiceChannel.channel,
                members: [
                  ...(activeVoiceChannel.members || []),
                  ...vc.members,
                ].map((m) =>
                  m.user._id === memberId
                    ? {
                        ...m,
                        ...info,
                      }
                    : m,
                ),
              }
            : vc,
        ),
      );
    },
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const disconnectVoiceChannel = useCallback(
    async (
      status: ConnectionStatus = 'disconnected',
      nextActiveVoiceChannel: VoiceChannel = {} as VoiceChannel,
      updatedActiveVoiceChannel?: VoiceChannel,
    ) => {
      const channelId = (updatedActiveVoiceChannel || activeVoiceChannel)
        ?.channel?._id;

      if (channelId) {
        removeMemberFromVoiceChannel(channelId, user?._id || '');
        io.emit('/voice-channel/leave', {
          channelId,
        });
      }

      setActiveVoiceChannel(nextActiveVoiceChannel);
      disconnect(status !== 'disconnected');

      setConnectionObject(null);
      setActiveTranscriptionChannelId(null);
      setActiveTranscriptionNote(null);
      setActiveTranscriptionOwner(undefined);
    },
    [
      io,
      activeVoiceChannel,
      user?._id,
      disconnect,
      setActiveVoiceChannel,
      setConnectionObject,
      setActiveTranscriptionChannelId,
      setActiveTranscriptionNote,
      setActiveTranscriptionOwner,
    ],
  );

  const checkMicrophone = useCallback(async () => {
    if (!hasMicrophone) {
      enqueueSnackbar(
        'You must have a microphone an grant access to then to enter in a voice channel!',
        {
          variant: 'warning',
        },
      );
      await grantPermission(DetectRTC.hasWebcam);
    }
    return true;
  }, [hasMicrophone, enqueueSnackbar, grantPermission]);

  const connectVoiceChannel = useCallback(
    async ({
      channel,
      isGuest = false,
      callOtherMembers = false,
    }: {
      channel: IChatConversation;
      isGuest: boolean;
      callOtherMembers: boolean;
    }) => {
      if (
        connectionStatus === 'connected' &&
        activeVoiceChannel?.channel?._id === channel._id
      )
        return;

      await checkMicrophone();

      const _connect = async () => {
        try {
          let connectionObject = guestConnectionObject;
          if (!isGuest) {
            connectionObject = await ChatRequests.getVoiceChannelToken(
              channel._id,
            );
            setConnectionObject(connectionObject);
          }

          if (!connectionObject?.token) {
            throw new Error('Error getting token');
          }

          io.emit('/voice-channel/join', {
            channelId: channel._id,
            callOtherMembers,
          });
          updateLocalParticipant({ socketId: io.id });
          setActiveVoiceChannel({ channel, members: [] });
          setConnectionStatus('connecting');

          if (micMuted !== undefined) {
            setPreConnectionOptions({ micOn: !micMuted });
          }

          createConnection();
          if (connectionObject?.metadata?.egressId) {
            setActiveVoiceChannel({
              channel: {
                ...channel,
                egressId: connectionObject?.metadata?.egressId,
              },
              members: [],
            });
          }
          if (connectionObject?.metadata?.notesDocument) {
            setActiveTranscriptionChannelId(channel._id);
            setActiveTranscriptionNote(
              connectionObject?.metadata?.notesDocument,
            );
          }
          if (connectionObject?.metadata?.noteStartedBy) {
            setActiveTranscriptionOwner(
              connectionObject?.metadata?.noteStartedBy,
            );
          }
        } catch (error) {
          enqueueSnackbar(
            'Error connecting to voice channel. Try again later.',
          );
          await disconnectVoiceChannel('disconnected', {} as VoiceChannel, {
            channel,
            members: [],
          });
          setConnectionStatus('disconnected');
        }
      };

      if (
        (connectionStatus === 'connected' &&
          activeVoiceChannel?.channel?._id !== channel._id) ||
        connectionStatus === 'error'
      ) {
        await disconnectVoiceChannel(
          'reconnecting',
          { channel, members: [] },
          activeVoiceChannel,
        );
        return;
      }

      _connect();
    },
    [
      activeVoiceChannel,
      connectionStatus,
      guestConnectionObject,
      io,
      createConnection,
      disconnectVoiceChannel,
      enqueueSnackbar,
      setActiveVoiceChannel,
      setConnectionStatus,
      updateLocalParticipant,
      setActiveTranscriptionChannelId,
      setActiveTranscriptionNote,
      setActiveTranscriptionOwner,
      setConnectionObject,
      micMuted,
      setPreConnectionOptions,
      checkMicrophone,
    ],
  );

  const connectAiCall = useCallback(
    async ({
      aiCallId,
      aiCallTitle,
      aiUser,
      isSchedule = false,
    }: AIScheduleInvite & { aiUser: IWorkspaceUser; isSchedule?: boolean }) => {
      if (
        connectionStatus === 'connected' &&
        activeVoiceChannel?.channel?._id === aiCallId
      )
        return;

      const channel: AICallChannel = {
        _id: aiCallId,
        name: aiCallTitle || aiUser.name,
        type: ChatChannelType.VOICE,
        aiCall: true,
        order: 0,
        participants: [{ user: user?._id as string }, { user: aiUser._id }],
        messages: [],
        hasUnreadMessages: false,
        description: '',
        createdAt: new Date().toISOString(),
        subchannels: [],
        roles: {
          admin: { [user?._id as string]: true, [aiUser._id]: true },
          viewer: {},
          member: {},
        },
        workspace: '',
        unreadMessages: [],
      };

      const members: VoiceChannelMember[] = [
        {
          user: {
            _id: user?._id as string,
            name: user?.name as string,
            email: user?.email as string,
            picture: user?.picture,
            active: true,
            workspace: user?.workspace as IWorkspace,
            title: user?.title as string,
            role: {
              _id: '',
              name: '',
              color: '',
            },
            status: user?.status as UserStatus,
            lastAccess: '',
            customStatus: user?.customStatus as CustomStatus,
            type: UserType.USER,
          },
          socketId: io.id as string,
          deaf: false,
          muted: false,
          isSpeaking: false,
        },
        {
          user: {
            _id: aiUser?._id as string,
            name: aiUser?.name as string,
            email: aiUser?.email as string,
            picture: aiUser?.picture,
            active: true,
            workspace: aiUser?.workspace as IWorkspace,
            title: aiUser?.title as string,
            role: {
              _id: '',
              name: '',
              color: '',
            },
            status: aiUser?.status as UserStatus,
            lastAccess: '',
            customStatus: aiUser?.customStatus as CustomStatus,
            type: UserType.AI,
          },
          socketId: '',
          deaf: false,
          muted: false,
          isSpeaking: false,
        },
      ];

      await checkMicrophone();

      const _connect = async () => {
        try {
          const connectionObject = await (isSchedule
            ? AIScheduleRequests.getAIScheduleCallToken(channel._id)
            : AIScheduleRequests.getAICallToken(channel._id));
          setConnectionObject(connectionObject);

          if (!connectionObject?.token) {
            throw new Error('Error getting token');
          }

          updateLocalParticipant({ socketId: io.id });
          setActiveVoiceChannel({ channel, members });
          setConnectionStatus('connecting');

          createConnection(LIVEKIT_AI_SERVER_URL);
        } catch (error) {
          enqueueSnackbar('Error connecting to AI Call. Try again later.');
          await disconnectVoiceChannel('disconnected', {} as VoiceChannel, {
            channel,
            members: [],
          });
          setConnectionStatus('disconnected');
        }
      };

      if (
        (connectionStatus === 'connected' &&
          activeVoiceChannel?.channel?._id !== channel._id) ||
        connectionStatus === 'error'
      ) {
        await disconnectVoiceChannel('disconnected', {} as VoiceChannel, {
          channel,
          members: [],
        });
        setConnectionStatus('disconnected');
      }

      if (!isSchedule) {
        setVoiceChannels((prevState) => [
          ...prevState.map((vc) =>
            vc.channel._id === aiCallId
              ? {
                  ...vc,
                  channel: { ...vc.channel, ...channel },
                  members: [...vc.members, ...members],
                }
              : vc,
          ),
        ]);
      }

      _connect();
    },
    [
      connectionStatus,
      enqueueSnackbar,
      disconnectVoiceChannel,
      setConnectionObject,
      updateLocalParticipant,
      io.id,
      setActiveVoiceChannel,
      setConnectionStatus,
      createConnection,
      user?._id,
      user?.name,
      user?.email,
      user?.picture,
      user?.workspace,
      user?.title,
      user?.status,
      user?.customStatus,
      activeVoiceChannel?.channel?._id,
      checkMicrophone,
    ],
  );

  const setMuted = useCallback(
    (newMutedValue: boolean, playSound = true) => {
      const { deaf } = localParticipant;

      if (deaf) {
        setDeaf(false);
      }
      setMute(newMutedValue);
      setMicMuted(newMutedValue);

      if (!playSound) return;
      audio.play(AudioType.CHANNEL_VOICE_MUTE, {
        skipLastPlayed: true,
      });
    },
    [localParticipant, audio, setDeaf, setMute, setMicMuted],
  );

  const stopCamera = useCallback(() => {
    const { camera } = localParticipant;
    if (!camera) return;

    stopVideo('camera');
    audio.play(AudioType.CHANNEL_VOICE_SCREEN_SHARING_END, {
      skipLastPlayed: true,
    });
  }, [localParticipant, audio, stopVideo]);

  const stopScreenShare = useCallback(() => {
    const { sharing } = localParticipant;
    if (!sharing) return;

    stopVideo();
    audio.play(AudioType.CHANNEL_VOICE_SCREEN_SHARING_END, {
      skipLastPlayed: true,
    });
  }, [localParticipant, audio, stopVideo]);

  const startCamera = useCallback(() => {
    const { camera, sharing } = localParticipant;

    if (camera) return;

    if (sharing) {
      stopScreenShare();
    }

    switchVideo('camera');
    audio.play(AudioType.CHANNEL_VOICE_SCREEN_SHARING_START, {
      skipLastPlayed: true,
    });
  }, [localParticipant, audio, switchVideo, stopScreenShare]);

  const startScreenShare = useCallback(() => {
    const { sharing, camera } = localParticipant;
    if (sharing) return;

    if (camera) {
      stopCamera();
    }

    switchVideo();
    audio.play(AudioType.CHANNEL_VOICE_SCREEN_SHARING_START, {
      skipLastPlayed: true,
    });
  }, [localParticipant, audio, switchVideo, stopCamera]);

  const setDeafed = useCallback(
    (newDeafedValue: boolean) => {
      const { muted } = localParticipant;
      setDeaf(newDeafedValue);
      audio.play(AudioType.CHANNEL_VOICE_DEAF, {
        skipLastPlayed: true,
      });

      if (newDeafedValue && muted) {
        setAlreadyMutedWhenDeaf(true);
      }
      if (newDeafedValue && !muted) {
        setMuted(true, false);
      }
      if (!newDeafedValue && !alreadyMutedWhenDeaf && muted) {
        setMuted(false, false);
      }
      if (!newDeafedValue) setAlreadyMutedWhenDeaf(false);
    },
    [alreadyMutedWhenDeaf, localParticipant, setMuted, setDeaf, audio],
  );

  useSocketEvent(
    startTakingNotes((socket) => {
      if (
        socket.document?.channelRef !== activeVoiceChannel.channel?._id ||
        activeTranscriptionOwner === user?._id
      )
        return;

      audio.play(AudioType.NOTE_TAKING_START);

      setActiveTranscriptionChannelId(socket.channel._id);
      setActiveTranscriptionOwner(socket.document.owner);
      setActiveTranscriptionNote(socket.document);
      setNote(socket.document);
    }),
  );

  useSocketEvent(
    onSocketReconnect(() => {
      if (activeVoiceChannel.channel?._id) {
        io.emit('/voice-channel/ensure-connected', {
          channelId: activeVoiceChannel.channel._id,
        });
      }
    }),
  );

  useSocketEvent(
    stopTakingNotes((socket) => {
      if (
        socket.channel?._id !== activeVoiceChannel.channel?._id ||
        activeTranscriptionOwner === user?._id
      )
        return;

      audio.play(AudioType.NOTE_TAKING_END);

      setActiveTranscriptionOwner(undefined);

      if (activeTranscriptionNote) {
        setActiveTranscriptionNote({
          ...activeTranscriptionNote,
          loadingTranscription: true,
        });
      }

      setActiveVoiceChannel({
        ...activeVoiceChannel,
        channel: {
          ...activeVoiceChannel.channel,
          egressId: undefined,
        },
      });
    }),
  );
  useSocketEvent(
    onMuteParticipant(({ channelId, sender }: VoiceControl) => {
      if (channelId !== activeVoiceChannel.channel?._id) return;
      setMuted(true);
      const userName = activeVoiceChannel.members.find(
        (member) => member.user._id === sender,
      )?.user.name;
      enqueueSnackbar(`${userName} muted your microphone`);
    }),
  );

  useSocketEvent(
    onKickParticipant(async ({ channelId, sender }: VoiceControl) => {
      if (channelId !== activeVoiceChannel.channel?._id) return;
      const userName = activeVoiceChannel.members.find(
        (member) => member.user._id === sender,
      )?.user.name;
      enqueueSnackbar(`You are kicked from channel by ${userName}`);
      await disconnectVoiceChannel();
    }),
  );

  const memoizedValue = useMemo(
    () => ({
      // Current active voice channel
      activeVoiceChannel,
      setActiveVoiceChannel,

      // Connection status
      connectionStatus,
      connectionQuality,
      // Mute

      setMuted,
      // Deaf
      setDeafed,
      // All Voice channels

      voiceChannels,
      setVoiceChannels,
      // Connect and Disconnect
      connectAiCall,
      connectVoiceChannel,
      disconnectVoiceChannel,
      // disconnectRtcOnly,
      startScreenShare,
      stopScreenShare,
      startCamera,
      stopCamera,
    }),
    [
      activeVoiceChannel,
      setActiveVoiceChannel,
      connectionStatus,
      connectionQuality,
      setMuted,
      setDeafed,
      voiceChannels,
      connectAiCall,
      connectVoiceChannel,
      disconnectVoiceChannel,
      startScreenShare,
      stopScreenShare,
      startCamera,
      stopCamera,
    ],
  );

  return (
    <VoiceChannelContext.Provider value={memoizedValue}>
      {children}
    </VoiceChannelContext.Provider>
  );
}
